/**
 * Reading a safety recommendation from the FAA's side: which status group it sits in, how its
 * status reads, and how long it has been open. Shared by the workbook registers and the explorer's
 * Safety Recommendations register.
 *
 * Status groups, the "why" excerpt and https links come from the server with each recommendation.
 */

import {
    SafetyRecommendationDTO,
    SafetyRecommendationStatusGroup,
    SafetyRecommendationYearCountDTO,
} from '../../@types/safety-recommendation';

/** Status groups from good to critical, then the rest: the order charts and legends use. */
export const STATUS_GROUP_ORDER: SafetyRecommendationStatusGroup[] =
    ['CLOSED_ACCEPTABLE', 'OPEN', 'OPEN_UNACCEPTABLE', 'CLOSED_UNACCEPTABLE', 'CLOSED_OTHER'];

export const STATUS_GROUP_LABELS: Record<SafetyRecommendationStatusGroup, string> = {
    OPEN: 'Open',
    OPEN_UNACCEPTABLE: 'Open - Unacceptable',
    CLOSED_ACCEPTABLE: 'Closed - Acceptable',
    CLOSED_UNACCEPTABLE: 'Closed - Unacceptable',
    CLOSED_OTHER: 'Closed - Other',
};

/** Status choices offered alongside the single statuses: sets of status groups under one name. */
export const STATUS_PRESETS: { key: string; label: string; groups: SafetyRecommendationStatusGroup[] }[] = [
    {key: 'open', label: 'Open', groups: ['OPEN', 'OPEN_UNACCEPTABLE']},
    {key: 'closed', label: 'Closed', groups: ['CLOSED_ACCEPTABLE', 'CLOSED_UNACCEPTABLE', 'CLOSED_OTHER']},
    {key: 'unacceptable', label: 'Unacceptable (open or closed)', groups: ['OPEN_UNACCEPTABLE', 'CLOSED_UNACCEPTABLE']},
];

export const ANY_STATUS = 'any';
export const MIXED_STATUS = 'mixed';

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

const sameGroups = (a: SafetyRecommendationStatusGroup[], b: SafetyRecommendationStatusGroup[]): boolean =>
    a.length === b.length && a.every((group) => b.includes(group));

/**
 * The status selector's value for a set of status groups: "any" for none, a preset's key, a single
 * group's name, or "mixed" for a combination picked on the chart that has no name of its own.
 */
export const statusSelection = (groups: SafetyRecommendationStatusGroup[]): string => {
    if (groups.length === 0) return ANY_STATUS;
    const preset = STATUS_PRESETS.find((p) => sameGroups(p.groups, groups));
    if (preset) return preset.key;
    return groups.length === 1 ? groups[0] : MIXED_STATUS;
};

/** The status groups a selector value stands for. */
export const groupsForSelection = (value: string): SafetyRecommendationStatusGroup[] => {
    if (value === ANY_STATUS) return [];
    const preset = STATUS_PRESETS.find((p) => p.key === value);
    if (preset) return [...preset.groups];
    return STATUS_GROUP_ORDER.includes(value as SafetyRecommendationStatusGroup) ? [value as SafetyRecommendationStatusGroup] : [];
};

/**
 * How many investigations issued these recommendations. Worth showing beside the count: one
 * investigation usually issues several, so 45 recommendations can be four accidents.
 */
export const countInvestigations = (recs: SafetyRecommendationDTO[]): number =>
    new Set(recs
        .map((rec) => rec.issuingCaseNumber)
        .filter((caseNumber): caseNumber is string => Boolean(caseNumber && caseNumber.trim()))).size;

/** Whether the FAA is an addressee: the server gives it a status group whenever it is. */
export const isAddressedToFaa = (rec: SafetyRecommendationDTO): boolean => Boolean(rec.faaStatusGroup);

/** The FAA's own status when it is an addressee, otherwise the overall status, in the source's wording. */
export const focusStatus = (rec: SafetyRecommendationDTO): string | undefined =>
    (isAddressedToFaa(rec) ? rec.faaStatus : rec.overallStatus);

/**
 * A status as a register displays it: "Closed - Acceptable Action" and "Closed - Unacceptable
 * Action" read without the trailing "Action". The LLM context keeps the source's wording.
 */
export const displayStatus = (status: string | undefined): string | undefined =>
    status?.replace(/^(Closed - (?:Unacceptable|Acceptable)) Action\b/, '$1');

export const isOpen = (rec: SafetyRecommendationDTO): boolean => rec.focusStatusGroup.startsWith('OPEN');

/**
 * Years the recommendation has been open (open) or took to close (closed), or null without an
 * issue date. Closed recommendations without a closed date count as null too.
 */
export const ageYears = (rec: SafetyRecommendationDTO, today: Date): number | null => {
    if (!rec.issuedDate) return null;
    const issued = new Date(rec.issuedDate).getTime();
    const end = isOpen(rec) ? today.getTime() : (rec.focusClosedDate ? new Date(rec.focusClosedDate).getTime() : NaN);
    if (isNaN(issued) || isNaN(end)) return null;
    return Math.max(0, (end - issued) / MS_PER_YEAR);
};

/** "open 12.4 yrs" or "closed after 3.1 yrs". */
export const ageLabel = (rec: SafetyRecommendationDTO, today: Date): string => {
    const years = ageYears(rec, today);
    if (years === null) return '';
    return isOpen(rec) ? `open ${years.toFixed(1)} yrs` : `closed after ${years.toFixed(1)} yrs`;
};

export interface IssuedByYear {
    /** Every year from the first to the last issue year, including years with none. */
    years: number[];
    /** Per status group, the count for each year in {@link IssuedByYear.years}. */
    counts: Record<SafetyRecommendationStatusGroup, number[]>;
}

export const emptyGroupRecord = <T>(value: () => T): Record<SafetyRecommendationStatusGroup, T> =>
    ({OPEN: value(), OPEN_UNACCEPTABLE: value(), CLOSED_ACCEPTABLE: value(), CLOSED_UNACCEPTABLE: value(), CLOSED_OTHER: value()});

/** The server's year counts as the timeline draws them: one array per group over its run of years. */
export const countsToIssuedByYear = (years: SafetyRecommendationYearCountDTO[]): IssuedByYear => {
    const counts = emptyGroupRecord((): number[] => years.map(() => 0));
    years.forEach((year, index) => {
        STATUS_GROUP_ORDER.forEach((group) => {
            counts[group][index] = year.byStatusGroup[group] ?? 0;
        });
    });
    return {years: years.map((year) => year.year), counts};
};
